// ./src/components/features/QrTags/QrTagsList/QrTagsList.tsx
//
// ROADMAP #172 — an event's QR tags: a table on desktop, cards below (buttons on a tablet, a swipe on a phone). Each row
// says the tag's number and code, what it is, who holds it (or that nobody does yet — or that its holder is gone), which
// session / date it is for, and when and by whom it was given out. Rights-gated per row; selection-ready (bulk unlink /
// remove).
//
// ⚠ "Unlink" is a do-something action (the tag stays, nobody holds it), so on a phone it is on the OTHER side from Edit /
//   Delete (`secondary: true`). So is "Give to…" (follow-up c, 2026-10-19) — offered on a tag NOBODY holds; a held one is
//   taken back first, or re-given from Edit.
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, ActionIcon, Tooltip, Badge, Skeleton, Table, Checkbox } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { QrTag } from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  TableDateTime,
  CardSkeletonActions,
  CardSkeletonCheckbox,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";

export interface QrTagsListProps {
  data: QrTag[];
  loading: boolean;
  /** ⚠ REQUIRED — whether the FIRST read has come back (the skeleton belongs to it alone). ▶ check-list-states */
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  /** True when a search or filter is narrowing the list — changes what "empty" means. */
  hasQueryNarrowing: boolean;
  /** The event's sessions / dates by id — "For" on each row. */
  slotNames: Record<string, string>;
  onEdit?: (tag: QrTag) => void;
  onDelete?: (tag: QrTag) => void;
  onUnlink?: (tag: QrTag) => void;
  /** Gives a tag nobody holds to a guest (the page's `…/:id/give` stage). */
  onGive?: (tag: QrTag) => void;
  /** First-run empty state's action. */
  onGenerate?: () => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  isRemoving: (id?: string) => boolean;
  highlightedId?: string | null;
  selection?: RowSelection;
  selectable?: boolean;
}

const tagTitle = (tag: QrTag): string => (tag.sequenceNo != null ? `#${tag.sequenceNo}` : "—");

interface RowProps {
  tag: QrTag;
  slotName: string | null;
  onEdit?: (tag: QrTag) => void;
  onDelete?: (tag: QrTag) => void;
  onUnlink?: (tag: QrTag) => void;
  onGive?: (tag: QrTag) => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

/** Who holds it — or that nobody does yet, or that the holder is gone (the tag points at nobody who can come in). */
const HolderCell: React.FC<{ tag: QrTag }> = ({ tag }) => {
  const { t } = useTranslation(["qrTags"]);
  const holder = tag.holder;

  if (!tag.targetId) {
    return (
      <Text size="md" c="dimmed">
        {t("qrTags:list.free", { defaultValue: "Not given out" })}
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
      <AppIcon iconName={tag.targetKind === "ticket" ? "Ticket" : "User"} size={16} />
      <Text size="md" fw={500} truncate td={holder?.inactive ? "line-through" : undefined}>
        {holder?.name || t("qrTags:list.unnamed", { defaultValue: "Unnamed guest" })}
      </Text>
      {holder?.inactive ? (
        <Badge size="sm" variant="light" color="gray" style={{ flexShrink: 0 }}>
          {tag.targetKind === "ticket" ? t("qrTags:list.ticketGone", { defaultValue: "Ticket no longer valid" }) : t("qrTags:list.guestGone", { defaultValue: "Removed" })}
        </Badge>
      ) : null}
    </Group>
  );
};

const CodeCell: React.FC<{ tag: QrTag }> = ({ tag }) => {
  const { t } = useTranslation(["qrTags"]);

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Group gap={6} wrap="nowrap">
        <Text size="md" ff="monospace" fw={600} truncate>
          {tag.code}
        </Text>
        {tag.origin === "adopted" ? (
          <Tooltip label={t("qrTags:list.adoptedHint", { defaultValue: "A code printed elsewhere (a card or wristband), added here" })} withArrow>
            <Badge size="sm" variant="light" color="grape" style={{ flexShrink: 0 }}>
              {t("qrTags:list.adopted", { defaultValue: "Own card" })}
            </Badge>
          </Tooltip>
        ) : null}
        {tag.isPublic ? (
          // #172 phase 2 — anyone's phone camera opens its page.
          <Tooltip label={t("qrTags:list.publicHint", { defaultValue: "Anyone who scans it with a phone sees its public page" })} withArrow>
            <Badge size="sm" variant="light" color="teal" style={{ flexShrink: 0 }}>
              {t("qrTags:list.public", { defaultValue: "Public" })}
            </Badge>
          </Tooltip>
        ) : null}
      </Group>
      {tag.label ? (
        <Text size="md" c="dimmed" truncate>
          {tag.label}
        </Text>
      ) : null}
    </Stack>
  );
};

const RowActions: React.FC<Pick<RowProps, "tag" | "onEdit" | "onDelete" | "onUnlink" | "onGive" | "isUpdating" | "isDeleting">> = ({
  tag,
  onEdit,
  onDelete,
  onUnlink,
  onGive,
  isUpdating,
  isDeleting,
}) => {
  const { t } = useTranslation(["qrTags", "common"]);
  const canEdit = recordHasRight(tag, "edit");
  const rowBusy = isUpdating(tag.id);

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      {onGive && canEdit && !tag.targetId ? (
        <Tooltip label={t("qrTags:actions.give", { defaultValue: "Give to a guest" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="indigo"
            size="lg"
            disabled={isDeleting || rowBusy}
            onClick={() => onGive(tag)}
            aria-label={t("qrTags:actions.give", { defaultValue: "Give to a guest" })}
          >
            <AppIcon iconName="UserPlus" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onUnlink && canEdit && tag.targetId ? (
        <Tooltip label={t("qrTags:actions.unlink", { defaultValue: "Take back (nobody holds it)" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="orange"
            size="lg"
            loading={isUpdating(tag.id, "unlink")}
            disabled={isDeleting || (rowBusy && !isUpdating(tag.id, "unlink"))}
            onClick={() => onUnlink(tag)}
            aria-label={t("qrTags:actions.unlink", { defaultValue: "Take back (nobody holds it)" })}
          >
            <AppIcon iconName="Unlink" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onEdit && canEdit ? (
        <Tooltip label={t("common:buttons.edit", { defaultValue: "Edit" })} withArrow>
          <ActionIcon variant="subtle" color="neutral" size="lg" disabled={isDeleting || rowBusy} onClick={() => onEdit(tag)} aria-label={t("common:buttons.edit", { defaultValue: "Edit" })}>
            <AppIcon iconName="Pencil" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onDelete && recordHasRight(tag, "remove") ? (
        <Tooltip label={t("common:buttons.delete", { defaultValue: "Delete" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="error"
            size="lg"
            loading={isDeleting}
            disabled={rowBusy}
            onClick={() => onDelete(tag)}
            aria-label={t("common:buttons.delete", { defaultValue: "Delete" })}
          >
            <AppIcon iconName="Trash" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
};

const QrTagTableRow: React.FC<RowProps> = (p) => (
  <Table.Tr
    style={{
      opacity: p.isDeleting ? 0.5 : 1,
      backgroundColor: p.highlighted ? HIGHLIGHT_ROW_BG : p.selected ? "var(--mantine-color-blue-light)" : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    {p.selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        {p.selectable && (
          <Checkbox checked={p.selected} onChange={() => p.tag.id && p.onToggleSelect?.(p.tag.id)} disabled={p.isDeleting} aria-label={p.tag.code ?? ""} />
        )}
      </Table.Td>
    )}
    <Table.Td style={{ width: 70 }}>
      <Text size="md" c="dimmed">
        {tagTitle(p.tag)}
      </Text>
    </Table.Td>
    <Table.Td>
      <CodeCell tag={p.tag} />
    </Table.Td>
    <Table.Td>
      <HolderCell tag={p.tag} />
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Text size="md" c={p.slotName ? undefined : "dimmed"} lineClamp={1}>
        {p.slotName ?? "—"}
      </Text>
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <TableDateTime value={p.tag.targetId ? p.tag.linkedAt : null} />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <RowActions tag={p.tag} onEdit={p.onEdit} onDelete={p.onDelete} onUnlink={p.onUnlink} onGive={p.onGive} isUpdating={p.isUpdating} isDeleting={p.isDeleting} />
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const QrTagCardComponent: React.FC<CardProps> = (p) => {
  const { t } = useTranslation(["qrTags", "common"]);
  const { tag } = p;
  const toggleSelect = () => tag.id && p.onToggleSelect?.(tag.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    const canEdit = recordHasRight(tag, "edit");

    if (p.onGive && canEdit && !tag.targetId) {
      actions.push({
        key: "give",
        icon: "UserPlus",
        label: t("qrTags:actions.giveShort", { defaultValue: "Give to…" }),
        color: "indigo",
        onClick: () => p.onGive?.(tag),
        disabled: p.isDeleting,
        secondary: true,
      });
    }

    if (p.onUnlink && canEdit && tag.targetId) {
      actions.push({
        key: "unlink",
        icon: "Unlink",
        label: t("qrTags:actions.unlinkShort", { defaultValue: "Take back" }),
        color: "orange",
        onClick: () => p.onUnlink?.(tag),
        loading: p.isUpdating(tag.id, "unlink"),
        disabled: p.isDeleting,
        secondary: true,
      });
    }

    if (p.onEdit && canEdit) {
      actions.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit", { defaultValue: "Edit" }), color: "blue", onClick: () => p.onEdit?.(tag), disabled: p.isDeleting });
    }

    if (p.onDelete && recordHasRight(tag, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete", { defaultValue: "Delete" }), color: "red", onClick: () => p.onDelete?.(tag), loading: p.isDeleting, disabled: p.isDeleting });
    }

    return actions;
  }, [p, tag, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={p.isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease",
        backgroundColor: p.selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(p.highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox mt={2} checked={p.selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={p.isDeleting} aria-label={tag.code ?? ""} />
        )}
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" justify="space-between">
            <CodeCell tag={tag} />
            <Text size="md" c="dimmed" style={{ flexShrink: 0 }}>
              {tagTitle(tag)}
            </Text>
          </Group>
          <Box mt={6}>
            <HolderCell tag={tag} />
          </Box>
          {p.slotName ? (
            <Text size="md" c="dimmed" mt={4} lineClamp={1}>
              {p.slotName}
            </Text>
          ) : null}
        </Box>
        {showActions && <RowActions tag={tag} onEdit={p.onEdit} onDelete={p.onDelete} onUnlink={p.onUnlink} onGive={p.onGive} isUpdating={p.isUpdating} isDeleting={p.isDeleting} />}
      </Group>
    </Paper>
  );

  if (p.selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={p.selectable ? toggleSelect : undefined} style={{ cursor: p.selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, p.selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, p.selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={p.forceClose}
            onForceCloseComplete={p.onForceCloseComplete}
            onSwipeStateChange={p.onSwipeStateChange}
            disabled={p.isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, p.selectable)}</Box>
    </>
  );
};

const QrTagCard = React.memo(QrTagCardComponent);

const QrTagsListComponent: React.FC<QrTagsListProps> = ({
  data,
  loading,
  isLoaded,
  loadingMore,
  hasMore,
  onLoadMore,
  hasQueryNarrowing,
  slotNames,
  onEdit,
  onDelete,
  onUnlink,
  onGive,
  onGenerate,
  isUpdating,
  isRemoving,
  highlightedId,
  selection,
  selectable = false,
}) => {
  const { t } = useTranslation(["qrTags", "common"]);
  const { isDesktop } = useAppEnvironment();
  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onUnlink, onGive, onEdit, onDelete);
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback(
    (tag: QrTag) => {
      closeAllCards();
      onEdit?.(tag);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (tag: QrTag) => {
      closeAllCards();
      onDelete?.(tag);
    },
    [onDelete, closeAllCards]
  );

  const handleUnlink = useCallback(
    (tag: QrTag) => {
      closeAllCards();
      onUnlink?.(tag);
    },
    [onUnlink, closeAllCards]
  );

  const handleGive = useCallback(
    (tag: QrTag) => {
      closeAllCards();
      onGive?.(tag);
    },
    [onGive, closeAllCards]
  );

  const rowIsSelectable = (tag: QrTag): boolean => isSelectable && ((!!onUnlink && recordHasRight(tag, "edit")) || (!!onDelete && recordHasRight(tag, "remove")));
  const selectableVisibleIds = data.filter(rowIsSelectable).map((tg) => tg.id).filter((id): id is string => !!id);

  const header = (
    <Table.Thead>
      <Table.Tr>
        {isSelectable && (
          <Table.Th style={{ width: 44 }}>
            {isLoaded ? (
              <Checkbox
                checked={selection!.allSelected(selectableVisibleIds)}
                indeterminate={selection!.someSelected(selectableVisibleIds)}
                onChange={() => selection!.toggleAll(selectableVisibleIds)}
                disabled={selectableVisibleIds.length === 0}
                aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
              />
            ) : null}
          </Table.Th>
        )}
        <Table.Th style={{ width: 70 }}>{t("qrTags:list.columns.number", { defaultValue: "No." })}</Table.Th>
        <Table.Th>{t("qrTags:list.columns.code", { defaultValue: "Code" })}</Table.Th>
        <Table.Th>{t("qrTags:list.columns.holder", { defaultValue: "Held by" })}</Table.Th>
        <Table.Th style={{ width: 180 }}>{t("qrTags:list.columns.slot", { defaultValue: "For" })}</Table.Th>
        <Table.Th style={{ width: 150 }}>{t("qrTags:list.columns.linkedAt", { defaultValue: "Given out" })}</Table.Th>
        <Table.Th style={{ width: 150 }} />
      </Table.Tr>
    </Table.Thead>
  );

  // ⚠ The FIRST read only. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="xs" aria-busy="true">
        <Box visibleFrom="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Table.Tr key={i}>
                    {isSelectable && (
                      <Table.Td>
                        <Skeleton height={20} width={20} radius="sm" />
                      </Table.Td>
                    )}
                    <Table.Td>
                      <Skeleton height={16} width={30} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={170} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="50%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="70%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={90} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap" justify="flex-end">
                        <Skeleton circle height={34} width={34} />
                        <Skeleton circle height={34} width={34} />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Box>
        {[0, 1, 2].map((i) => (
          // The phone card: code (+ label) and number, then the holder.
          <Paper key={`p-${i}`} withBorder radius="md" p="md" hiddenFrom="sm">
            <Group gap="md" wrap="nowrap" align="flex-start">
              <CardSkeletonCheckbox show={isSelectable} />
              <CardSkeletonLines count={2} gap={6} />
            </Group>
          </Paper>
        ))}
        {[0, 1, 2].map((i) => (
          <Paper key={`t-${i}`} withBorder radius="md" p="md" visibleFrom="sm" hiddenFrom="md">
            <Group gap="md" wrap="nowrap" justify="space-between">
              <CardSkeletonCheckbox show={isSelectable} />
              <CardSkeletonLines count={2} gap={6} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("qrTags:list.noMatch", { defaultValue: "No tags match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("qrTags:list.emptyTitle", { defaultValue: "No QR tags yet" })}
          description={t("qrTags:list.emptyBody", {
            defaultValue:
              "Generate codes to print on wristbands, cards or items — or scan cards you already have at the door. Hand one to a guest at check-in and scan it any time to see whose it is.",
          })}
          iconName="Qrcode"
          actionLabel={onGenerate ? t("qrTags:actions.generate", { defaultValue: "Generate tags" }) : undefined}
          onAction={onGenerate}
          size="md"
        />
      </Paper>
    );
  }


  const rowProps = (tag: QrTag): RowProps => ({
    tag,
    slotName: tag.eventTimeSlotId ? (slotNames[tag.eventTimeSlotId] ?? null) : null,
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    onUnlink: onUnlink ? handleUnlink : undefined,
    onGive: onGive ? handleGive : undefined,
    isUpdating,
    highlighted: tag.id === highlightedId,
    isDeleting: isRemoving(tag.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(tag),
    selected: selection?.isSelected(tag.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            {header}
            <Table.Tbody>
              {data.map((tag) => (
                <QrTagTableRow key={tag.id} {...rowProps(tag)} />
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {loadMore}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((tag) => (
          <QrTagCard
            key={tag.id}
            {...rowProps(tag)}
            forceClose={shouldForceClose(tag.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(tag.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(tag.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const QrTagsList = React.memo(QrTagsListComponent);
